import React, { useState } from 'react';
import { toast } from 'sonner';
import { EmptyState } from '../../../components/common/EmptyState';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { TabsContent } from '../../../components/ui/tabs';
import { getStatusLabel } from '../../../lib/utils';
import { ChevronDown, ChevronUp, Download, Edit, Home, Layers, Pencil, Plus, Search, Table, X } from 'lucide-react';
import api from '../../../lib/api';
import { BaseCompletaDialog } from '../../../components/BaseCompletaDialog';

const StatusBadge = ({ ok, okLabel = 'OK', missLabel = 'Falta' }) => {
  const cls = ok
    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : 'bg-orange-50 text-orange-600 border-orange-200';
  return (
    <span className={`inline-flex items-center px-1.5 py-0 text-[10px] leading-4 rounded border font-medium ${cls}`}>
      {ok ? okLabel : missLabel}
    </span>
  );
};

const NeutralBadge = ({ children, tone = 'gray' }) => {
  const cls = tone === 'green'
    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : 'bg-slate-50 text-slate-500 border-slate-200';
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0 text-[10px] leading-4 rounded border font-medium whitespace-nowrap max-w-[90px] truncate ${cls}`}
      title={typeof children === 'string' ? children : undefined}
    >
      {children}
    </span>
  );
};

const propietarioResumen = (u) => {
  // Modelo nuevo (multi-propietario): u.propietarios = [{propietario_id, propietario_nombre, porcentaje}, ...]
  const nuevos = (u.propietarios || []).filter(p => p && p.propietario_nombre);
  if (nuevos.length > 0) return nuevos;
  // Fallback legacy (single-owner): propietario_id + propietario_nombre planos en el doc.
  if (u.propietario_id && u.propietario_nombre) {
    return [{
      propietario_id: u.propietario_id,
      propietario_nombre: u.propietario_nombre,
      porcentaje: 100,
    }];
  }
  return [];
};

export const TabUnidades = ({
  busquedaOficina,
  edificio,
  expandedGrupos,
  filtroTipo,
  grupos,
  handleOpenAltaRapida,
  handleOpenEditGrupo,
  handleOpenEditUnidad,
  handleOpenGrupo,
  handleOpenPropietario,
  handleRemovePropietario,
  setBusquedaOficina,
  setDeleteGrupoTarget,
  setFiltroTipo,
  setIsDeleteUnidadOpen,
  setSelectedUnidad,
  sortUnidades,
  toggleGrupoExpanded,
  unidades,
}) => {
  const [exportingExcel, setExportingExcel] = useState(false);
  const [isBaseCompletaOpen, setIsBaseCompletaOpen] = useState(false);

  const handleExportExcel = async () => {
    if (!edificio?.edificio_id) return;
    setExportingExcel(true);
    try {
      const res = await api.get(`/edificios/${edificio.edificio_id}/unidades/export-excel`, {
        responseType: 'blob',
      });
      const cd = res.headers['content-disposition'] || '';
      const match = cd.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `unidades_${edificio.edificio_id}.xlsx`;
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Excel descargado: ${filename}`);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Error al exportar Excel');
    } finally {
      setExportingExcel(false);
    }
  };
  const Th = ({ k, children, className, width }) => (
    <th
      className={`sticky top-0 z-20 bg-slate-50 text-[10px] font-semibold uppercase tracking-wider text-slate-500 py-2 px-1.5 select-none shadow-[0_1px_0_0_rgb(226,232,240)] ${className || ''}`}
      style={width ? { width } : undefined}
      data-testid={k ? `th-${k}` : undefined}
    >
      <span className="inline-flex items-center">{children}</span>
    </th>
  );

  const hasSize = (u) =>
    Number(u.m2_cubiertos || 0) > 0 ||
    Number(u.m2_semicubiertos || 0) > 0 ||
    Number(u.m2_descubiertos || 0) > 0 ||
    Number(u.m2_balcon || 0) > 0 ||
    Number(u.m2_totales || 0) > 0 ||
    Number(u.m2_alquiler || 0) > 0 ||
    Number(u.m2_expensas || 0) > 0;
  const hasCarta = (u) => !!(u.carta_oferta_archivo?.filename || u.carta_oferta_archivo?.data);
  const hasPosesion = (u) => !!(u.posesion_archivo?.filename || u.posesion_archivo?.data);
  const hasEscritura = (u) => !!u.escritura_archivo;
  // Servicios OK: al menos un campo de NIC/Tasa cargado con valor, o marcado como "No aplica" (eso indica revisión).
  const hasServicios = (u) =>
    !!(u.nic_energia_electrica || u.nic_agua || u.tasa_municipal || u.tasa_comercial) ||
    !!(u.nic_energia_no_aplica || u.nic_agua_no_aplica || u.tasa_municipal_no_aplica || u.tasa_comercial_no_aplica);
  // General OK (Torre Emilia): nombre + tipo (siempre presentes) + nomenclatura catastral cargada.
  const hasGeneral = (u) =>
    !!u.nombre_unidad &&
    !!u.tipo &&
    !!(u.nomenclatura_catastral && String(u.nomenclatura_catastral).trim());
  // Inversión OK (Torre Emilia): fecha de adquisición + al menos un propietario con monto neto > 0.
  const hasInversion = (u) =>
    !!u.fecha_adquisicion &&
    Array.isArray(u.propietarios) &&
    u.propietarios.some(p => Number(p?.monto_total_compra?.monto || 0) > 0);
  // Datos cargados completos (Torre Emilia): General + Superficies + Inversión + Servicios.
  // NO incluye Documentación (Carta/Posesión/Escritura no afectan este estado).
  const hasDatosCompletosTE = (u) =>
    hasGeneral(u) && hasSize(u) && hasInversion(u) && hasServicios(u);

  // ── Bifurcación por modelo de administración (Fase 2 — Torre Emilia) ──
  // Torre Emilia (gc_factura_y_cobra): tabla plana. "Piso 1" y "Cocheras Subsuelo"
  // existen ahora como unidades sintéticas reales en DB (rol_unidad='principal'); sus
  // miembros internos quedan vinculados via unidad_principal_id y visible=false.
  //   - Excluimos del listado todo lo que tenga unidad_principal_id (componentes).
  //   - Excluimos todo lo que tenga visible_en_listado_unidades=false explícitamente.
  // La columna "Cocheras" solo cuenta cocheras vinculadas a un principal.
  // Foresta (propietario_factura_directo): conserva la tabla agrupada legacy intacta.
  const esTorreEmilia = edificio?.modelo_administracion === 'gc_factura_y_cobra';

  const unidadesVisibles = esTorreEmilia
    ? unidades.filter(u =>
        !u.unidad_principal_id &&
        u.visible_en_listado_unidades !== false
      )
    : unidades;

  const cocherasCountByUnidad = (() => {
    if (!esTorreEmilia) return null;
    const map = {};
    for (const u of unidades) {
      if (u.tipo === 'cochera' && u.unidad_principal_id) {
        map[u.unidad_principal_id] = (map[u.unidad_principal_id] || 0) + 1;
      }
    }
    return map;
  })();

  return (
    <TabsContent value="config" className="space-y-3">
      {/* Toolbar: filtros + búsqueda + botones — todo en una sola fila */}
      <div className="flex items-center gap-2 flex-nowrap md:flex-nowrap flex-wrap">
        {/* Filtros */}
        <div className="flex gap-1 shrink-0">
          {[
            { value: 'todas', label: 'Todas' },
            { value: 'oficina', label: 'Oficinas' },
            { value: 'cowork', label: 'Cowork' },
            { value: 'local', label: 'Locales' },
            { value: 'cochera', label: 'Cocheras' },
          ].map(opt => {
            const count = opt.value === 'todas' ? unidadesVisibles.length : unidadesVisibles.filter(u => u.tipo === opt.value).length;
            if (opt.value !== 'todas' && count === 0) return null;
            return (
              <Button key={opt.value} variant={filtroTipo === opt.value ? 'default' : 'outline'} size="sm" onClick={() => setFiltroTipo(opt.value)} className="text-xs h-9">
                {opt.label}<span className="ml-1 text-xs opacity-70">({count})</span>
              </Button>
            );
          })}
        </div>

        {/* Buscador */}
        <div className="relative flex-1 min-w-[140px] max-w-[280px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input type="text" placeholder="Buscar por oficina o propietario" value={busquedaOficina} onChange={(e) => setBusquedaOficina(e.target.value)} className="pl-9 pr-7 h-9 text-sm w-full" data-testid="busqueda-oficinas" />
          {busquedaOficina && (
            <button onClick={() => setBusquedaOficina('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Botones */}
        <div className="flex items-center gap-2 ml-auto shrink-0">
          <Button
            onClick={() => setIsBaseCompletaOpen(true)}
            disabled={!edificio?.edificio_id}
            variant="outline"
            size="sm"
            className="h-9"
            data-testid="ver-base-completa-unidades-btn"
          >
            <Table className="w-4 h-4 mr-1" />
            Ver base
          </Button>
          <Button
            onClick={handleExportExcel}
            disabled={exportingExcel || !edificio?.edificio_id}
            variant="outline"
            size="sm"
            className="h-9"
            data-testid="export-unidades-excel-btn"
          >
            <Download className="w-4 h-4 mr-1" />
            {exportingExcel ? 'Exportando…' : 'Exportar Excel'}
          </Button>
          <Button onClick={handleOpenAltaRapida} data-testid="create-alta-rapida-btn" size="sm" className="h-9">
            <Plus className="w-4 h-4 mr-1" /> Alta unidades
          </Button>
        </div>
      </div>

      {unidades.length === 0 ? (
        <EmptyState icon={Home} title="Sin unidades" description="Este edificio no tiene unidades configuradas." action={<Button onClick={handleOpenAltaRapida}><Plus className="w-4 h-4 mr-2" />Alta de Unidades</Button>} />
      ) : (
        <div className="space-y-3 flex-1 min-h-0 flex flex-col">
          {(() => {
            let lst = esTorreEmilia ? [...unidadesVisibles] : [...unidades];
            if (filtroTipo !== 'todas') lst = lst.filter(u => u.tipo === filtroTipo);
            if (busquedaOficina.trim()) {
              const q = busquedaOficina.toLowerCase().trim();
              lst = lst.filter(u => {
                const nm = String(u.nombre_unidad || '').toLowerCase();
                const propLeg = String(u.propietario_nombre || '').toLowerCase();
                const propAll = (u.propietarios || []).map(p => String(p.propietario_nombre || '').toLowerCase()).join(' | ');
                return nm.includes(q) || propLeg.includes(q) || propAll.includes(q);
              });
            }
            lst = sortUnidades(lst);
            if (lst.length === 0) {
              return (
                <div className="bg-slate-50 rounded-lg p-6 text-center">
                  <p className="text-slate-500">
                    {busquedaOficina.trim() ? `No se encontraron resultados para "${busquedaOficina}"` : `No hay unidades de tipo "${filtroTipo}"`}
                  </p>
                </div>
              );
            }

            const tipoPrio = (t) => ({ oficina: 0, cowork: 1, local: 2, cochera: 3 })[String(t || '').toLowerCase()] ?? 4;
            // Orden canónico de unidades (igual que 5.Factura): Piso 1 primero,
            // luego oficinas por número (201..402), y al final Piso 5, 6, 7.
            const ordenUnidad = (nombre, tipo) => {
              const tp = tipoPrio(tipo);
              const n = String(nombre || '').trim();
              if (/^piso\s*1\b/i.test(n)) return [tp, 0, 0];
              const soloNum = n.match(/^(\d+)/);
              if (soloNum) return [tp, 1, parseInt(soloNum[1], 10)];
              const mPiso = n.match(/piso\s*(\d+)/i);
              if (mPiso) return [tp, 2, parseInt(mPiso[1], 10)];
              return [tp, 3, 9999];
            };
            const cmpOrden = (ka, kb) => {
              for (let i = 0; i < ka.length; i++) {
                if (ka[i] !== kb[i]) return ka[i] - kb[i];
              }
              return 0;
            };

            // Render columna Propietario (con quitar inline y agregar/cambiar)
            const renderPropietarios = (u) => {
              const lista = propietarioResumen(u);
              if (lista.length === 0) {
                return (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-red-600 font-medium">Falta cargar</span>
                    <Button size="icon" variant="ghost" className="h-6 w-6 text-slate-500 hover:text-slate-900" onClick={() => handleOpenPropietario(u)} data-testid={`add-prop-${u.unidad_id}`} title="Asignar propietario">
                      <Plus className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                );
              }
              return (
                <div className="flex flex-wrap items-center gap-1">
                  {lista.map(p => (
                    <span key={p.propietario_id} className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] rounded bg-slate-100 text-slate-800 border border-slate-200" title={p.propietario_nombre}>
                      <span className="truncate max-w-[150px]">{p.propietario_nombre}</span>
                      {lista.length > 1 && (
                        <span className="text-slate-500">{p.porcentaje}%</span>
                      )}
                      <button
                        type="button"
                        className="text-slate-400 hover:text-red-600"
                        onClick={() => handleRemovePropietario && handleRemovePropietario(u, p.propietario_id)}
                        data-testid={`remove-prop-${u.unidad_id}-${p.propietario_id}`}
                        title="Quitar este propietario de la unidad"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                  <Button size="icon" variant="ghost" className="h-6 w-6 text-slate-500 hover:text-slate-900" onClick={() => handleOpenPropietario(u)} data-testid={`edit-props-${u.unidad_id}`} title="Cambiar / agregar propietario">
                    <Pencil className="w-3 h-3" />
                  </Button>
                </div>
              );
            };

            // ─── Rama Torre Emilia: tabla plana, sin grupos, con columna "Cocheras" (count) ───
            if (esTorreEmilia) {
              const filasTE = [...lst].sort((a, b) =>
                cmpOrden(ordenUnidad(a.nombre_unidad, a.tipo), ordenUnidad(b.nombre_unidad, b.tipo))
              );

              const renderRowTE = (u) => {
                const n = cocherasCountByUnidad[u.unidad_id] || 0;
                const datosOk = hasDatosCompletosTE(u);
                return (
                  <tr key={u.unidad_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`unidad-row-${u.unidad_id}`}>
                    {/* Unidad */}
                    <td className="py-1.5 px-2">
                      <p className="text-sm font-medium text-slate-900 whitespace-nowrap" title={u.nombre_unidad}>{u.nombre_unidad}</p>
                    </td>
                    {/* Propietario */}
                    <td className="py-1.5 px-2">{renderPropietarios(u)}</td>
                    {/* Tipo */}
                    <td className="py-1.5 px-2 text-center">
                      <span className="px-2 py-0.5 rounded-full text-[11px] bg-slate-100 text-slate-700">{getStatusLabel(u.tipo)}</span>
                    </td>
                    {/* Cocheras */}
                    <td className="py-1.5 px-2 text-center" data-testid={`cocheras-count-${u.unidad_id}`}>
                      {n > 0
                        ? <span className="text-sm font-medium text-slate-900">{n}</span>
                        : <span className="text-slate-300">—</span>}
                    </td>
                    {/* Datos cargados */}
                    <td className="py-1.5 px-2 text-center" data-testid={`datos-cargados-${u.unidad_id}`}>
                      <StatusBadge ok={datosOk} />
                    </td>
                    {/* Editar */}
                    <td className="py-1.5 px-2 text-center">
                      <Button variant="outline" size="sm" onClick={() => handleOpenEditUnidad(u)} data-testid={`datos-btn-${u.unidad_id}`}>
                        <Pencil className="w-3 h-3 mr-1" /> Editar
                      </Button>
                    </td>
                  </tr>
                );
              };

              return (
                <div className="bg-white border border-slate-200 rounded-lg overflow-hidden flex-1 min-h-0 flex flex-col" data-testid="tabla-unidades-te">
                  <div className="flex-1 min-h-0 overflow-y-auto">
                    <table className="w-full min-h-full">
                      <thead className="sticky top-0 z-10 bg-slate-50">
                        <tr className="border-b border-slate-200">
                          <Th k="unidad" className="text-left" width="14%">Unidad</Th>
                          <Th k="propietario" className="text-left">Propietario</Th>
                          <Th k="tipo" className="text-center" width="10%">Tipo</Th>
                          <Th k="cocheras_count" className="text-center" width="10%">Cocheras</Th>
                          <Th k="datos_cargados" className="text-center" width="12%">Datos cargados</Th>
                          <Th className="text-center" width="10%">Editar</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {filasTE.map(u => renderRowTE(u))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            }

            // ─── Rama Foresta (legacy, intacta): tabla con grupos ───
            // Grupos y filas
            const renderedGrupoUnidadIds = new Set();
            const gruposConU = grupos.map(g => {
              const gU = g.unidad_ids.map(id => lst.find(u => u.unidad_id === id)).filter(Boolean);
              gU.forEach(u => renderedGrupoUnidadIds.add(u.unidad_id));
              return { ...g, unidades: gU };
            }).filter(g => g.unidades.length > 0);
            const sueltas = lst.filter(u => !renderedGrupoUnidadIds.has(u.unidad_id));

            const filas = [
              ...gruposConU.map(g => ({ tipo: 'grupo', data: g, orden: g.orden ?? 999999, nombre: g.nombre })),
              ...sueltas.map(u => ({ tipo: 'unidad', data: u, orden: u.orden ?? 999999, nombre: u.nombre_unidad })),
            ].sort((a, b) => {
              const tA = a.tipo === 'grupo' ? a.data?.unidades?.[0]?.tipo : a.data?.tipo;
              const tB = b.tipo === 'grupo' ? b.data?.unidades?.[0]?.tipo : b.data?.tipo;
              const nA = a.tipo === 'grupo' ? (a.data?.unidades?.[0]?.nombre_unidad || a.nombre) : (a.data?.nombre_unidad || a.nombre);
              const nB = b.tipo === 'grupo' ? (b.data?.unidades?.[0]?.nombre_unidad || b.nombre) : (b.data?.nombre_unidad || b.nombre);
              return cmpOrden(ordenUnidad(nA, tA), ordenUnidad(nB, tB));
            });

            const renderRow = (u, inGroup = false) => (
              <tr key={u.unidad_id} className={`border-b border-slate-100 hover:bg-slate-50 ${inGroup ? 'bg-indigo-50/40' : ''}`} data-testid={`unidad-row-${u.unidad_id}`}>
                <td className="py-1.5 px-2">
                  <p className="text-sm font-medium text-slate-900 whitespace-nowrap" title={u.nombre_unidad}>{inGroup ? '↳ ' : ''}{u.nombre_unidad}</p>
                </td>
                <td className="py-1.5 px-2">{renderPropietarios(u)}</td>
                <td className="py-1.5 px-2 text-center">
                  <span className="px-2 py-0.5 rounded-full text-[11px] bg-slate-100 text-slate-700">{getStatusLabel(u.tipo)}</span>
                </td>
                <td className="py-1.5 px-2 text-center">
                  <Button variant="outline" size="sm" onClick={() => handleOpenEditUnidad(u)} data-testid={`datos-btn-${u.unidad_id}`}>
                    <Pencil className="w-3 h-3 mr-1" /> Editar
                  </Button>
                </td>
                <td className="py-1.5 px-2 text-center"><StatusBadge ok={hasCarta(u)} /></td>
                <td className="py-1.5 px-2 text-center"><StatusBadge ok={hasPosesion(u)} /></td>
                <td className="py-1.5 px-2 text-center"><StatusBadge ok={hasEscritura(u)} /></td>
                <td className="py-1.5 px-2 text-center"><StatusBadge ok={hasSize(u)} /></td>
                <td className="py-1.5 px-2 text-center"><StatusBadge ok={hasServicios(u)} /></td>
                <td className="py-1.5 px-2 text-center">
                  {u.tiene_cochera
                    ? <NeutralBadge tone="green">{u.cochera_numero ? `Nº ${u.cochera_numero}` : 'Sí'}</NeutralBadge>
                    : <NeutralBadge>No tiene</NeutralBadge>}
                </td>
                <td className="py-1.5 px-2 text-center">
                  {u.tiene_amoblamiento
                    ? <NeutralBadge tone="green">OK</NeutralBadge>
                    : <NeutralBadge>No tiene</NeutralBadge>}
                </td>
              </tr>
            );

            return (
              <div className="bg-white border border-slate-200 rounded-lg overflow-hidden flex-1 min-h-0 flex flex-col">
                <div className="flex-1 min-h-0 overflow-y-auto">
                  <table className="w-full min-h-full">
                    <thead className="sticky top-0 z-10 bg-slate-50">
                      <tr className="border-b border-slate-200">
                        <Th k="unidad" className="text-left" width="11%">Unidad</Th>
                        <Th k="propietario" className="text-left">Propietario</Th>
                        <Th k="tipo" className="text-center" width="7%">Tipo</Th>
                        <Th className="text-center" width="7%">Datos</Th>
                        <Th k="carta_oferta" className="text-center" width="7%">Carta</Th>
                        <Th k="posesion" className="text-center" width="7%">Posesión</Th>
                        <Th k="escritura" className="text-center" width="8%">Escritura</Th>
                        <Th k="tamano" className="text-center" width="7%">Tamaño</Th>
                        <Th k="servicios" className="text-center" width="8%">Servicios</Th>
                        <Th k="cochera" className="text-center" width="10%">Cochera</Th>
                        <Th k="amoblamiento" className="text-center" width="7%">Amobl.</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {filas.map(f => {
                        if (f.tipo === 'unidad') return renderRow(f.data);
                        const g = f.data;
                        const first = g.unidades[0];
                        return (
                          <React.Fragment key={g.grupo_id}>
                            <tr className="bg-indigo-50 border-b border-indigo-200 cursor-pointer hover:bg-indigo-100/60" onClick={() => toggleGrupoExpanded(g.grupo_id)}>
                              <td className="py-1.5 px-2">
                                <div className="flex items-center gap-2">
                                  {expandedGrupos[g.grupo_id] ? <ChevronUp className="w-4 h-4 text-indigo-400" /> : <ChevronDown className="w-4 h-4 text-indigo-400" />}
                                  <Layers className="w-4 h-4 text-indigo-600" />
                                  <span className="text-sm font-semibold text-indigo-900">{g.nombre}</span>
                                  <span className="text-[10px] text-indigo-500 bg-indigo-100 px-1.5 py-0 rounded-full">{g.unidades.length} uds</span>
                                </div>
                              </td>
                              <td className="py-1.5 px-2">{first ? renderPropietarios(first) : '-'}</td>
                              <td className="py-1.5 px-2 text-center"><span className="text-xs text-indigo-500">Grupo</span></td>
                              <td className="py-1.5 px-2 text-center">
                                <div className="inline-flex gap-1">
                                  <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); handleOpenEditGrupo(g); }}>
                                    <Edit className="w-3.5 h-3.5 mr-1" />Editar
                                  </Button>
                                  <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={(e) => { e.stopPropagation(); setDeleteGrupoTarget(g.grupo_id); }}>
                                    <X className="w-3.5 h-3.5" />Desagrupar
                                  </Button>
                                </div>
                              </td>
                              <td colSpan={6} className="py-1.5 px-2 text-center text-xs text-slate-400">—</td>
                            </tr>
                            {expandedGrupos[g.grupo_id] && g.unidades.map(u => renderRow(u, true))}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      <BaseCompletaDialog
        open={isBaseCompletaOpen}
        onClose={() => setIsBaseCompletaOpen(false)}
        title={`Base completa de Unidades — ${edificio?.nombre || ''}`}
        endpoint={`/edificios/${edificio?.edificio_id}/unidades/preview-base`}
        firstColumnIndex={1}
        rowIdKey="unidad_id"
      />
    </TabsContent>
  );
};
